import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { apiClient } from '../apiClient';
import { Mail, Lock } from 'lucide-react';
import BrandMark from '../components/BrandMark';
import { safeRedirectPath } from '../utils/navigation';
import { SKOOL_SIGNUP_URL } from '../config/externalLinks';

function LoginPage({ setUser }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = safeRedirectPath(new URLSearchParams(location.search).get('redirect'));

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await apiClient.post('/api/auth/login', { email, password });
      setUser(data.user);
      navigate(redirect || '/profile');
    } catch (err) {
      setError(err.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8 animate-fade-in">
          <div className="flex justify-center mb-4">
            <BrandMark size={72} className="auth-logo" />
          </div>
          <h1 className="text-3xl font-bold mb-2 text-gradient">Welcome Back</h1>
          <p className="text-gray-400">Sign in to Rebel Prompts</p>
        </div>

        <div className="card-glass animate-fade-in">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Email Address</label>
              <div className="input-with-icon">
                <Mail className="icon" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-glass"
                  placeholder="you@example.com"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-300">Password</label>
                <Link to="/forgot-password" className="text-xs text-rebel-blue hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="input-with-icon">
                <Lock className="icon" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-glass"
                  placeholder="********"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-gray-400">Don't have an account? </span>
            <a href={SKOOL_SIGNUP_URL} target="_blank" rel="noreferrer" className="text-rebel-blue hover:underline font-semibold">
              Sign up
            </a>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-gray-400 hover:text-white transition-colors">
            {'<-'} Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
